import React from 'react'
import type { DateRange } from 'react-day-picker'
import { Calendar as CalendarIcon, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatDateLabelStable } from 'helpers/dateTime'
import { normalizeAppLanguage } from 'helpers/documentLocale'
import { cn } from '../lib/utils'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'

/** Parse YYYY-MM-DD as a browser-local calendar date, without UTC midnight shifts. */
export function parseCalendarDate(value: string): Date | undefined {
 const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
 if (!match) return undefined
 const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
 return Number.isNaN(date.getTime()) ? undefined : date
}

/** Calendar selections are plain Y-M-D values; do not convert them through UTC. */
export function calendarDateToYmd(date: Date): string {
 return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export type DatePickerMode = 'single' | 'range'

type Props = {
 id?: string
 value: string | null
 onChange: (value: string | null) => void
 mode?: DatePickerMode
 placeholder?: string
 disabled?: boolean
 className?: string
 clearable?: boolean
 minDate?: string
 maxDate?: string
 error?: string
 'aria-label'?: string
 'aria-invalid'?: boolean
 'aria-describedby'?: string
}

function parseRangeValue(value: string | null): DateRange | undefined {
 if (!value) return undefined
 const parts = value.split(/\s+to\s+|\s+-\s+/).map((part) => part.trim()).filter(Boolean)
 if (parts.length !== 2) return undefined
 const from = parseCalendarDate(parts[0])
 const to = parseCalendarDate(parts[1])
 return from && to ? { from, to } : undefined
}

function formatDateValue(value: string | null, mode: DatePickerMode): string {
 if (!value) return ''
 if (mode === 'range') {
  const range = parseRangeValue(value)
  return range?.from && range.to
   ? `${formatDateLabelStable(calendarDateToYmd(range.from))} — ${formatDateLabelStable(calendarDateToYmd(range.to))}`
   : ''
 }
 return formatDateLabelStable(value)
}

export function DatePicker({
 id,
 value,
 onChange,
 mode = 'single',
 placeholder,
 disabled,
 className,
 clearable = true,
 minDate,
 maxDate,
 error,
 'aria-label': ariaLabel,
 'aria-invalid': ariaInvalid,
 'aria-describedby': ariaDescribedBy,
}: Props) {
 const { t, i18n } = useTranslation()
 const lang = normalizeAppLanguage(i18n.language)
 const rtl = lang === 'ar'
 const [open, setOpen] = React.useState(false)
 const [draftRange, setDraftRange] = React.useState<DateRange | undefined>(() => parseRangeValue(value))
 const [month, setMonth] = React.useState<Date>(() => {
  if (mode === 'range') return parseRangeValue(value)?.from ?? parseRangeValue(value)?.to ?? new Date()
  return (value && parseCalendarDate(value)) || new Date()
 })
 const label = formatDateValue(value, mode)
 const resolvedPlaceholder = placeholder ?? t('pos.datePicker.placeholder')
 const errorDescriptionId = error ? `${id || 'date-picker'}-error` : undefined
 const describedBy = [ariaDescribedBy, errorDescriptionId].filter(Boolean).join(' ') || undefined

 React.useEffect(() => {
  if (!open) {
   setDraftRange(parseRangeValue(value))
   if (mode === 'single' && value) setMonth(parseCalendarDate(value) ?? new Date())
   if (mode === 'range') setMonth(parseRangeValue(value)?.from ?? parseRangeValue(value)?.to ?? new Date())
  }
 }, [open, value, mode])

 const calendarDisabled = React.useCallback((date: Date) => {
  const ymd = calendarDateToYmd(date)
  if (minDate && ymd < minDate) return true
  if (maxDate && ymd > maxDate) return true
  return false
 }, [minDate, maxDate])

 const handleSingleSelect = (date: Date | undefined) => {
  if (!date) return
  const next = calendarDateToYmd(date)
  onChange(next)
  setOpen(false)
 }

 const handleRangeSelect = (range: DateRange | undefined) => {
  setDraftRange(range)
  if (range?.from && range.to) {
   onChange(`${calendarDateToYmd(range.from)} to ${calendarDateToYmd(range.to)}`)
   setOpen(false)
  }
 }

 return (
  <div className="relative min-w-0" dir={rtl ? 'rtl' : 'ltr'}>
   <Popover
    open={open}
    onOpenChange={(next) => {
     if (next) {
      setDraftRange(parseRangeValue(value))
      if (mode === 'single' && value) setMonth(parseCalendarDate(value) ?? new Date())
      if (mode === 'range') setMonth(parseRangeValue(value)?.from ?? parseRangeValue(value)?.to ?? new Date())
     }
     setOpen(next)
    }}
   >
    <PopoverTrigger asChild>
     <button
      id={id}
      type="button"
      disabled={disabled}
      aria-label={ariaLabel ?? (label || resolvedPlaceholder)}
      aria-expanded={open}
      aria-describedby={describedBy}
      className={cn(
       'flex h-10 min-h-10 w-full items-center rounded-xl border border-border-subtle bg-background px-3 py-2 text-sm shadow-sm',
       'text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0',
       clearable && value && 'pe-10',
       disabled && 'cursor-not-allowed opacity-60',
       (ariaInvalid || error) && 'border-destructive focus-visible:ring-destructive',
       className,
      )}
     >
      <CalendarIcon className="me-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span dir="ltr" className={cn('min-w-0 flex-1 truncate tabular-nums [unicode-bidi:isolate]', !label && 'text-muted-foreground')}>
       {label || resolvedPlaceholder}
      </span>
     </button>
    </PopoverTrigger>
    <PopoverContent
     align="start"
     sideOffset={4}
     className="z-[110000] w-auto max-w-[calc(100vw-1rem)] p-0"
     dir={rtl ? 'rtl' : 'ltr'}
    >
     {mode === 'range' ? (
      <Calendar
       mode="range"
       selected={draftRange}
       onSelect={handleRangeSelect}
       month={month}
       onMonthChange={setMonth}
       disabled={calendarDisabled}
       dir={rtl ? 'rtl' : 'ltr'}
       numberOfMonths={1}
       autoFocus
      />
     ) : (
      <Calendar
       mode="single"
       selected={value ? parseCalendarDate(value) : undefined}
       onSelect={handleSingleSelect}
       month={month}
       onMonthChange={setMonth}
       disabled={calendarDisabled}
       dir={rtl ? 'rtl' : 'ltr'}
       autoFocus
      />
     )}
    </PopoverContent>
   </Popover>

   {clearable && value && !disabled ? (
    <button
     type="button"
     className="absolute end-2 top-1/2 z-10 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
     onClick={() => onChange(null)}
     aria-label={t('pos.datePicker.clear')}
    >
     <X className="h-4 w-4" />
    </button>
   ) : null}
   {error ? <p id={errorDescriptionId} role="alert" className="mt-1 text-xs text-destructive">{error}</p> : null}
  </div>
 )
}
